import React, {useEffect, useMemo, useState} from "react";
import {useMakePageRequest} from "./RequestHooks";
import {useChangeContext} from "./RouteHooks";
import {selectParentContext} from "../Redux/Selectors/contextSelectors";
import {useSelector} from "react-redux";
import {ConfirmationDialog} from "../Components/Common/ConfirmationDialog";
import {useEffectPromise} from "./EffectHooks";
import {useIsAuthenticated} from "../../../monitoring/src/Hooks/useIsAuthenticated";

export const usePrimeCache = (services) => {
    
    const isAuthenticated = useIsAuthenticated();
    const displayTimeout = useSelector(state => state.loginState.displayTimeout);
    const [loadedCount, setLoadedCount] = useState(0);

    if(!(services instanceof Array)) {
        services = [services];
    }

    services = services.filter(service => {
        if (!service?.PrimeCache) {
            console.log('Warning: ' + service?.ModelName + 'Service does not support PrimeCache.');
            return false;
        }
        return true;
    });

    // Re-prime whenever the session recovers, not only on the isAuthenticated
    // edge. When the server rejects a still-client-valid token (e.g. permissions
    // changed elsewhere) isAuthenticated never flips, so keying only off it would
    // leave reference caches empty after a successful re-login — blanking every
    // page that gates on primed data. Skipping the prime while timed out and
    // re-running when displayTimeout clears recovers the cache. The counter (vs a
    // set-once boolean) guarantees a re-render so consumers that read the cache
    // directly during render pick up the freshly primed values.
    useEffect(() => {
        if (displayTimeout) {
            return;
        }
        Promise.allSettled(services.map(service => service.PrimeCache()))
            .then(() => {
                setLoadedCount(count => count + 1);
            });
    }, [isAuthenticated, displayTimeout]);

    return loadedCount > 0;
};

export const useCachedServices = (services) => {
    if(!(services instanceof Array)) {
        services = [services];
    }
    
    const loaded = usePrimeCache(services);
    
    return useMemo(() =>
        services.map( (service) => 
            loaded ? service.CachedValues()?.result : null
        ), [loaded, services]
    ); 
}

/**
 * Hook to fetch a model from a service using the provided id.
 * If a bootstrapped model is provided, it will be used as the initial value.
 * @param {Object} service - The service to fetch the model from.
 * @param {number|null} id - The id of the model to fetch.
 * @param {Object} bootstrappedModel - The initial value of the model.
 * @param useCache
 * @returns {Object} - The fetched model or the bootstrapped model if it exists.
 */
export const useFetchServiceModel = (service, id, bootstrappedModel = null, useCache = true) => {
    const [model, setModel] = useState(null);
    
    const primaryKey = service.PrimaryKeyProperty;
    
    // factors that affect reloading
    const expectedId = bootstrappedModel?.[primaryKey] ?? id;
    const modelDoesntMatchIdProp = (model == null) || Number(model[primaryKey]) !== Number(expectedId);
    
    // conditions upon which we should reload the model
    const shouldFetchModel = (
        !bootstrappedModel && // skip fetch if a bootstrapped model was provided
        expectedId > 0 && // there is valid id to fetch
        (!model || modelDoesntMatchIdProp) // AND we don't have a model matching it
    );
    
    useEffectPromise(
        async () => (await service.FindById(expectedId, useCache))?.result,
        setModel,
        [shouldFetchModel, expectedId, useCache],
        shouldFetchModel
    );
    
    return bootstrappedModel ?? model;
};

export const useDeleteModelConfirmationDialog = () => {
    const [visible, setVisible] = useState(false);

    const DeleteModelConfirmationDialog = props => {

        const {service, modelId} = props;
        
        const makePageRequest = useMakePageRequest();
        const changeContext = useChangeContext();
        const parentContext = useSelector(state => selectParentContext(state));
        
        const onClose = () => {
            setVisible(false);
        }
        
        const onConfirm = () => {
            makePageRequest(
                () => service.Delete(modelId),
                (x) => { changeContext(parentContext); }
            );
        };
        
        return <ConfirmationDialog
            Title="Confirmation"
            Body={`Are you sure you want to delete this ${service.ModelName}?`}
            Display={visible}
            OnClose={props.onClose ?? onClose}
            OnConfirm={props.onConfirm ?? onConfirm}
        />;
    };

    return [DeleteModelConfirmationDialog, setVisible];
};
